"use client";

import React from "react";
import { motion } from "framer-motion";
import { Globe as Globe2, Search, ChartBar as BarChart3, ArrowRight, Layers, Languages } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const pillars = [{
  icon: Languages,
  title: "Per-Market Research Over Direct Translation",
  description: "Translating your existing English keywords into another language misses how native speakers actually search. Even across English-speaking markets, vocabulary diverges sharply: UK searchers look for 'solicitor', 'trainers', 'car hire', and 'holiday', whereas US users search for 'lawyer', 'sneakers', 'car rental', and 'vacation'. Non-English markets require native-speaker analysis; for example, German searchers distinguish between 'Brautkleider' and 'Hochzeitskleider' depending on commercial intent and context."
}, {
  icon: Search,
  title: "Local Search Intent & SERP Analysis",
  description: "Search volume and intent differ by territory. A term that is purely informational in the UK may carry strong transactional intent in the US or Germany, or vice versa. I examine local search results pages manually alongside data from tools such as Google Keyword Planner, Google Search Console, and Ahrefs or Semrush to understand what local search engines actually reward for each query."
}, {
  icon: Layers,
  title: "Mapping Keywords to Prevent Cannibalisation",
  description: "Every target keyword group must map to a specific localized URL to prevent internal conflict between your domestic and international pages. By aligning each market's distinct search intent with dedicated landing pages, search engines can clearly discern which URL serves which territory, eliminating keyword cannibalisation and mixed indexation signals."
}];
export default function InternationalKeywordResearch() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="internationalkeywordresearch" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 lg:py-32 overflow-hidden border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-80px"
      }} className="space-y-16">
          {/* Header Block */}
          <motion.div variants={itemVariants} className="max-w-3xl space-y-4">
            <Badge variant="secondary" className="px-3 py-1 text-xs uppercase tracking-wider font-semibold bg-secondary text-secondary-foreground">
              Market-Specific Search Strategy
            </Badge>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default">
              International Keyword Research
            </h2>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed pt-2">
              International keyword research is never a translate-once exercise. Direct translations fail because native
              speakers use distinct cultural terms, local colloquialisms, and regional query patterns. Even within the
              same language, intent and vocabulary shift across borders—from UK “trainers”, “solicitor”, and “car hire”
              to North American “sneakers”, “lawyer”, and “car rental”. In non-English markets, native-speaker input and
              first-hand SERP analysis are essential to uncover genuine demand patterns, such as the distinct search
              behaviours behind German “Brautkleider” and “Hochzeitskleider”. I map every target term to specific
              market URLs to ensure clean indexation without cross-border cannibalisation.
            </p>
          </motion.div>

          {/* Three-Column Grid */}
          <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {pillars.map((pillar, i) => {
            const Icon = pillar.icon;
            return <motion.div key={pillar.title} variants={itemVariants} data-index={i}>
                  <Card className="h-full bg-card text-card-foreground border-border/80 shadow-xs hover:shadow-md hover:border-primary/40 transition-all duration-200">
                    <CardHeader className="space-y-4 pb-2">
                      <div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center text-primary shrink-0">
                        <Icon className="size-6 text-primary" />
                      </div>
                      <CardTitle className="text-xl font-semibold tracking-tight text-foreground">
                        <h3 className="text-xl font-semibold tracking-tight text-foreground">{pillar.title}</h3>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                        {pillar.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </motion.div>

          {/* Contextual Link Footer Note */}
          <motion.div variants={itemVariants} className="pt-4 border-t border-border/60 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-sm text-muted-foreground">
            <p>
              I research search volume, local competitor strength, and native query patterns directly for your target territories.
            </p>
            <Link to="/SeoAuditsPage" className="inline-flex items-center gap-1.5 font-medium text-primary hover:underline underline-offset-4 shrink-0">
              Start with an SEO Audit
              <ArrowRight className="size-4" />
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}